import React, { useState } from 'react';
import { 
  Search, Filter, Download, ChevronLeft, ChevronRight, 
  Plus, Edit, Trash2, Eye, Copy, Calendar, Percent,
  Tag, Clock, CheckCircle, XCircle, AlertCircle,
  DollarSign, Users, ShoppingBag, TrendingUp
} from 'lucide-react';

const Coupons = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const itemsPerPage = 5;

  // Static Mock Data
  const [coupons, setCoupons] = useState([
    {
      id: 'CPN-001',
      code: 'WELCOME20',
      description: 'Welcome discount for new customers',
      discountType: 'percentage',
      discountValue: 20,
      minPurchase: 500,
      maxDiscount: 500,
      usedCount: 145,
      totalLimit: 500,
      startDate: 'Jan 1, 2024',
      expiryDate: 'Dec 31, 2024',
      status: 'active',
      applicableCategories: ['All'],
      createdAt: 'Jan 1, 2024'
    },
    {
      id: 'CPN-002',
      code: 'FESTIVE50',
      description: 'Festive season special',
      discountType: 'percentage',
      discountValue: 50,
      minPurchase: 1000,
      maxDiscount: 1000,
      usedCount: 89,
      totalLimit: 200,
      startDate: 'Oct 1, 2024',
      expiryDate: 'Oct 31, 2024',
      status: 'active',
      applicableCategories: ['All'],
      createdAt: 'Sep 15, 2024'
    },
    {
      id: 'CPN-003',
      code: 'SAVE100',
      description: 'Flat ₹100 off on orders above ₹999',
      discountType: 'fixed',
      discountValue: 100,
      minPurchase: 999,
      maxDiscount: 100,
      usedCount: 234,
      totalLimit: 1000,
      startDate: 'Jan 1, 2024',
      expiryDate: 'Dec 31, 2024',
      status: 'active',
      applicableCategories: ['Indoor Plants', 'Outdoor Plants'],
      createdAt: 'Jan 1, 2024'
    },
    {
      id: 'CPN-004',
      code: 'FIRSTORDER',
      description: 'First order discount',
      discountType: 'percentage',
      discountValue: 15,
      minPurchase: 0,
      maxDiscount: 300,
      usedCount: 567,
      totalLimit: 1000,
      startDate: 'Jan 1, 2024',
      expiryDate: 'Mar 31, 2024',
      status: 'expired',
      applicableCategories: ['All'],
      createdAt: 'Jan 1, 2024'
    },
    {
      id: 'CPN-005',
      code: 'FREESHIP',
      description: 'Free shipping on orders above ₹500',
      discountType: 'shipping',
      discountValue: 0,
      minPurchase: 500,
      maxDiscount: 0,
      usedCount: 312,
      totalLimit: 500,
      startDate: 'Jan 1, 2024',
      expiryDate: 'Dec 31, 2024',
      status: 'inactive',
      applicableCategories: ['All'],
      createdAt: 'Jan 1, 2024'
    },
  ]);

  const stats = {
    total: coupons.length,
    active: coupons.filter(c => c.status === 'active').length,
    expired: coupons.filter(c => c.status === 'expired').length,
    inactive: coupons.filter(c => c.status === 'inactive').length,
    totalUsed: coupons.reduce((sum, c) => sum + c.usedCount, 0),
    totalDiscountGiven: 125000
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'active': return { label: 'Active', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'expired': return { label: 'Expired', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'inactive': return { label: 'Inactive', icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
      default: return { label: status, icon: Tag, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getDiscountDisplay = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}% OFF`;
    } else if (coupon.discountType === 'fixed') {
      return `₹${coupon.discountValue} OFF`;
    } else {
      return 'Free Shipping';
    }
  };

  const filteredCoupons = coupons.filter(coupon => {
    const matchesSearch = coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          coupon.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || coupon.discountType === filterType;
    const matchesStatus = filterStatus === 'all' || coupon.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);
  const paginatedCoupons = filteredCoupons.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const handleDelete = (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      setCoupons(coupons.filter(c => c.id !== couponId));
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    alert('Coupon code copied to clipboard!');
  };

  const getExpiryStatus = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    if (expiry < today) return 'expired';
    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7) return 'expiring-soon';
    return 'valid';
  };

  return (
    <div className="admin-coupons-modern">
      {/* Header */}
      <div className="coupons-header">
        <div>
          <h1 className="page-title">Coupons & Offers</h1>
          <p className="page-subtitle">Manage discount coupons and promotional offers</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Coupon
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Coupons" value={stats.total} icon={Tag} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Active" value={stats.active} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Expired" value={stats.expired} icon={XCircle} color="#ef4444" bgColor="#fee2e2" />
        <StatCard title="Total Used" value={stats.totalUsed} icon={ShoppingBag} color="#f59e0b" bgColor="#fed7aa" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by coupon code or description..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
            <option value="shipping">Free Shipping</option>
          </select>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredCoupons.length} Coupons Found</span>
      </div>

      {/* Coupons Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="coupons-table">
            <thead>
              <tr>
                <th>Coupon Code</th>
                <th>Description</th>
                <th>Discount</th>
                <th>Min. Purchase</th>
                <th>Usage</th>
                <th>Expiry Date</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCoupons.map((coupon) => {
                const statusConfig = getStatusConfig(coupon.status);
                const StatusIcon = statusConfig.icon;
                const expiryStatus = getExpiryStatus(coupon.expiryDate);
                
                return (
                  <tr key={coupon.id}>
                    <td className="coupon-code-cell">
                      <div className="coupon-code" onClick={() => handleCopyCode(coupon.code)}>
                        <span className="code">{coupon.code}</span>
                        <Copy size={14} className="copy-icon" />
                      </div>
                    </td>
                    <td className="description-cell">
                      <div className="description">{coupon.description}</div>
                    </td>
                    <td className="discount-cell">
                      <span className="discount-badge">
                        {getDiscountDisplay(coupon)}
                      </span>
                    </td>
                    <td className="min-purchase-cell">
                      {coupon.minPurchase > 0 ? `₹${coupon.minPurchase.toLocaleString()}` : 'No minimum'}
                    </td>
                    <td className="usage-cell">
                      <div className="usage-info">
                        <span className="used">{coupon.usedCount}</span>
                        <span className="separator">/</span>
                        <span className="total">{coupon.totalLimit}</span>
                      </div>
                      <div className="usage-bar">
                        <div className="usage-fill" style={{ width: `${(coupon.usedCount / coupon.totalLimit) * 100}%` }}></div>
                      </div>
                    </td>
                    <td className="expiry-cell">
                      <div className={`expiry-date ${expiryStatus}`}>
                        <Calendar size={12} />
                        <span>{coupon.expiryDate}</span>
                      </div>
                    </td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button className="action-btn edit" onClick={() => {
                          setSelectedCoupon(coupon);
                          setShowEditModal(true);
                        }} title="Edit Coupon">
                          <Edit size={16} />
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(coupon.id)} title="Delete Coupon">
                          <Trash2 size={16} />
                        </button>
                        <button className="action-btn view" title="View Details">
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCoupons.length)} of {filteredCoupons.length} coupons
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Coupon Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedCoupon(null);
        }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showCreateModal ? 'Create New Coupon' : 'Edit Coupon'}</h3>
              <button className="modal-close" onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedCoupon(null);
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Coupon Code</label>
                <input type="text" className="form-input" placeholder="e.g., SUMMER20" defaultValue={selectedCoupon?.code || ''} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-textarea" rows="2" placeholder="Brief description of the coupon" defaultValue={selectedCoupon?.description || ''} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Discount Type</label>
                  <select className="form-select" defaultValue={selectedCoupon?.discountType || 'percentage'}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                    <option value="shipping">Free Shipping</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Discount Value</label>
                  <input type="number" className="form-input" placeholder="e.g., 20" defaultValue={selectedCoupon?.discountValue || ''} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Minimum Purchase (₹)</label>
                  <input type="number" className="form-input" placeholder="0 for no minimum" defaultValue={selectedCoupon?.minPurchase || 0} />
                </div>
                <div className="form-group">
                  <label>Maximum Discount (₹)</label>
                  <input type="number" className="form-input" placeholder="Max discount amount" defaultValue={selectedCoupon?.maxDiscount || ''} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" className="form-input" defaultValue={selectedCoupon?.startDate || ''} />
                </div>
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input type="date" className="form-input" defaultValue={selectedCoupon?.expiryDate || ''} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Usage Limit (Total)</label>
                  <input type="number" className="form-input" placeholder="Total number of uses" defaultValue={selectedCoupon?.totalLimit || 1000} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-select" defaultValue={selectedCoupon?.status || 'active'}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Applicable Categories (Optional)</label>
                <select className="form-select" multiple size="3">
                  <option>All Categories</option>
                  <option>Indoor Plants</option>
                  <option>Outdoor Plants</option>
                  <option>Flowering Plants</option>
                  <option>Air Purifying Plants</option>
                  <option>Garden Tools</option>
                </select>
                <small className="form-hint">Hold Ctrl/Cmd to select multiple</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedCoupon(null);
              }}>Cancel</button>
              <button className="btn-primary" onClick={() => {
                alert(showCreateModal ? 'Coupon created!' : 'Coupon updated!');
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedCoupon(null);
              }}>{showCreateModal ? 'Create Coupon' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default Coupons;